import React, { useMemo } from 'react';
import { Box, Button, ButtonBase, Paper, Stack, Typography } from '@mui/material';
import { PointOfSale } from '@mui/icons-material';

import { StudentProfileResponse } from '../../../services/studentProfileService';
import { useCurrencyFormat } from '../../../hooks/useCurrencyFormat';
import PersonPhoto from '../../people/PersonPhoto';
import { ToneChip, statusLabel } from '../../common/StatusChip';
import { PERSON_KIND_LABEL, PERSON_KIND_TONE } from '../../families/familyFormat';
import { currentEnrollment, placementName } from './enrollmentPlacement';
import { feeTotals } from './profileFees';
import { ProfileTab } from './profileNav';

interface Props {
  data: StudentProfileResponse;
  /** Photo can be changed (student exists and the user may edit enrollments). */
  canEditPhoto: boolean;
  /** Offer "Collect fee" when something is owed, else "Receive advance". */
  canCollect: boolean;
  onCollect: (advance: boolean) => void;
  onOpenTab: (tab: ProfileTab) => void;
  onChanged: () => void;
}

const PARENT_OF: Record<string, string> = { male: 'Son of', female: 'Daughter of' };

/** Who this is at a glance, and the three things staff check first: class, fees, letters. */
const ProfileHeader: React.FC<Props> = ({ data, canEditPhoto, canCollect, onCollect, onOpenTab, onChanged }) => {
  const money = useCurrencyFormat();
  const { person, guardians, student, enrollments, admissions, fees, letters } = data;

  const current = currentEnrollment(enrollments);
  const totals = useMemo(() => feeTotals(fees), [fees]);
  const unpaidFines = letters.filter((l: any) => l.amount > 0 && (l.paymentStatus === 'unpaid' || l.paymentStatus === 'partial')).length;
  const father = guardians.find(g => g.relation === 'father') || guardians[0];
  const parentLine = father ? `${PARENT_OF[person.gender] || 'Child of'} ${father.name}` : null;
  // The GR number once enrolled; until then the latest application's number.
  const reference = person.grNumber ? `GR ${person.grNumber}` : admissions[0]?.applicationNumber;

  const tiles: Array<{ tab: ProfileTab; label: string; value: string; caption: string; color?: string }> = [
    {
      tab: 'academics',
      label: 'Class',
      value: placementName(current) || 'Not enrolled',
      caption: current
        ? [current.sessionId?.name, current.rollNumber != null && `Roll ${current.rollNumber}`].filter(Boolean).join(' · ')
        : admissions[0] ? `Application: ${statusLabel({ kind: 'application', value: admissions[0].status })}` : 'No application',
    },
    {
      tab: 'fees',
      label: 'Fees',
      value: totals.outstanding > 0 ? `${money(totals.outstanding)} due` : 'Clear',
      color: totals.outstanding > 0 ? 'warning.main' : 'success.main',
      caption: totals.outstanding > 0
        ? `${totals.openCount} challan${totals.openCount === 1 ? '' : 's'} open`
        : totals.paid > 0 ? `${money(totals.netPaid)} paid in all${totals.refunded > 0 ? ' after refunds' : ''}` : 'Nothing billed yet',
    },
    {
      tab: 'letters',
      label: 'Letters',
      value: letters.length ? `${letters.length} issued` : 'None',
      color: unpaidFines ? 'warning.main' : undefined,
      caption: unpaidFines ? `${unpaidFines} fine${unpaidFines === 1 ? '' : 's'} unpaid` : letters.length ? 'All settled' : 'No letters issued',
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <PersonPhoto
          name={person.name}
          caption={reference}
          photoFilename={person.photoFilename}
          owner={canEditPhoto && student ? { student: student._id } : undefined}
          onChanged={onChanged}
          size={88}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="h5" fontWeight={700} sx={{ wordBreak: 'break-word' }}>{person.name}</Typography>
            <ToneChip label={PERSON_KIND_LABEL[person.kind]} tone={PERSON_KIND_TONE[person.kind]} />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {parentLine}
            {parentLine && reference && ' · '}
            {reference && <Box component="span" sx={{ fontFamily: 'monospace' }}>{reference}</Box>}
          </Typography>
        </Box>
        {canCollect && (totals.outstanding > 0 ? (
          <Button variant="contained" disableElevation startIcon={<PointOfSale />} onClick={() => onCollect(false)} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Collect fee
          </Button>
        ) : (
          <Button variant="outlined" startIcon={<PointOfSale />} onClick={() => onCollect(true)} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Receive advance
          </Button>
        ))}
      </Stack>

      <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {tiles.map(t => (
          <ButtonBase
            key={t.tab}
            onClick={() => onOpenTab(t.tab)}
            sx={{
              display: 'block', textAlign: 'left', borderRadius: 2, px: 2, py: 1.5,
              border: 1, borderColor: 'divider', transition: 'border-color 120ms, background-color 120ms',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="caption" color="text.secondary">{t.label}</Typography>
            <Typography variant="subtitle1" fontWeight={700} color={t.color || 'text.primary'} noWrap>{t.value}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{t.caption}</Typography>
          </ButtonBase>
        ))}
      </Box>
    </Paper>
  );
};

export default ProfileHeader;
